import React from 'react';
import {
  serializeNodes,
  serializeNodesToText,
} from '@plone/volto-slate/editor/render';
import config from '@plone/volto/registry';
import isEqual from 'lodash/isEqual';
import Slugger from 'github-slugger';
import { normalizeString } from '@plone/volto/helpers/Utils/Utils';
import { useGridListStyle } from '../GridListStyleContext';

/**
 * Custom TextBlockView that applies list style CSS classes
 */
const TextBlockView = (props) => {
  const { id, data, styling = {}, className } = props;
  const { value, override_toc } = data;
  const metadata = props.metadata || props.properties;
  const { topLevelTargetElements } = config.settings.slate;

  // Get grid list style from context (if text block is inside a grid)
  const { gridListStyle } = useGridListStyle();

  // Note: Style classes are now handled by volto-block-style's StyleWrapper
  // We only need to handle list-specific styles here

  // Determine which list style to use: grid style takes precedence, then block style
  const effectiveListStyle =
    gridListStyle && gridListStyle !== 'default'
      ? gridListStyle
      : data.styles?.list_style;
  const hasListStyle = effectiveListStyle && effectiveListStyle !== 'default';

  // Only use className prop, don't add style classes (they're on the outer StyleWrapper)
  const wrapperClassName = className || '';

  // Helper function to parse date/time from text
  const parseTimeFromText = (text) => {
    // Match patterns like "2024:", "January 2024:", "2024-01-01:", etc.
    const timePatterns = [
      /^(\d{4}):?\s*/, // Year: "2024:" or "2024 "
      /^([A-Z][a-z]+ \d{4}):?\s*/, // Month Year: "January 2024:"
      /^(\d{1,2}\/\d{1,2}\/\d{4}):?\s*/, // Date: "01/15/2024:"
      /^(\d{4}-\d{2}-\d{2}):?\s*/, // ISO Date: "2024-01-15:"
      /^([A-Z][a-z]+ \d{1,2}, \d{4}):?\s*/, // Full Date: "January 15, 2024:"
    ];

    for (const pattern of timePatterns) {
      const match = text.match(pattern);
      if (match) {
        return {
          time: match[1],
          remainingText: text.replace(pattern, '').trim(),
        };
      }
    }
    return null;
  };

  const getAttributes = (node, path) => {
    const res = { ...styling };

    // Apply list style classes directly to list elements (but not timeline, which has its own class)
    if (
      node.type &&
      (node.type === 'ul' ||
        node.type === 'ol' ||
        node.type === 'bulleted-list' ||
        node.type === 'numbered-list')
    ) {
      if (hasListStyle) {
        // Add the list style class to the list element
        const listStyleClass = `has--list_style--${effectiveListStyle}`;
        res.className = res.className
          ? `${res.className} ${listStyleClass}`
          : listStyleClass;
      }
    }

    // Timeline nodes already have their class applied in the element renderer
    // No additional styling needed here for timeline type

    // Existing logic for top-level elements (TOC anchors)
    if (node.type && isEqual(path, [0])) {
      if (topLevelTargetElements.includes(node.type) || override_toc) {
        const text = serializeNodesToText(node?.children || []);
        const slug = Slugger.slug(normalizeString(text));
        res.id = slug || id;
      }
    }

    return res;
  };

  // Process content - check if it's a list pattern in paragraphs
  const processContent = () => {
    // Check if the value contains list nodes or paragraph nodes with list markers
    if (hasListStyle && value && value.length > 0) {
      // First check for actual list nodes
      const hasListNodes = value.some(
        (node) =>
          node.type === 'ul' ||
          node.type === 'ol' ||
          node.type === 'bulleted-list' ||
          node.type === 'numbered-list',
      );

      if (!hasListNodes) {
        // Look for list markers in text content
        const listItems = [];
        let currentText = '';

        value.forEach((node) => {
          if (node.children) {
            node.children.forEach((child) => {
              if (child.text) {
                currentText += child.text;
              }
            });
          }
        });

        // Split by line breaks and process each line
        const lines = currentText.split(/\n|\r\n|\r/);
        lines.forEach((line) => {
          const trimmedLine = line.trim();
          if (trimmedLine.startsWith('*') || trimmedLine.startsWith('•')) {
            // Remove the bullet marker and add as list item
            const itemText = trimmedLine.substring(1).trim();
            if (itemText) {
              listItems.push(itemText);
            }
          } else if (/^\d+\./.test(trimmedLine)) {
            // Remove number and dot for numbered lists
            const itemText = trimmedLine.replace(/^\d+\.\s*/, '').trim();
            if (itemText) {
              listItems.push(itemText);
            }
          }
        });

        if (listItems.length > 0) {
          // Render as proper list with the correct class
          const listClass = `has--list_style--${effectiveListStyle}`;
          return (
            <div className={wrapperClassName}>
              <ul className={listClass}>
                {listItems.map((item, index) => {
                  // Default rendering for other list styles
                  return <li key={index}>{item}</li>;
                })}
              </ul>
            </div>
          );
        }
      }
    }

    // Default serialization
    return serializeNodes(value, getAttributes, {
      metadata: metadata,
    });
  };
  return processContent();
};

export default TextBlockView;
